// FaqView — admin gerencia o catálogo de FAQ exibido pro aluno.
//
// Funcionalidades:
//   - Toggle global isEnabled (em /api/manager/faq-config)
//   - Lista de entries com toggle de publicado, edit, delete, subir/descer
//   - Modal pra criar/editar (textarea + preview do markdown live)
//
// Reorder usa botões ↑/↓ por enquanto — sem dependência de dnd-kit. Migra pra
// drag-and-drop quando dnd-kit entrar no projeto.

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Eye,
  EyeOff,
  HelpCircle,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  X
} from "lucide-react";
import { Skeleton } from "@app/ui/components/skeleton";
import {
  api,
  ApiError,
  type AdminFaqEntry,
  type FaqConfig
} from "./api";
import { openAlert, openConfirm } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

interface ListState {
  phase: "idle" | "loading" | "ready" | "error";
  entries: AdminFaqEntry[];
  config: FaqConfig;
  errMsg: string | null;
}

const listState = signal<ListState>({
  phase: "idle",
  entries: [],
  config: { isEnabled: false, updatedAt: null, updatedBy: null },
  errMsg: null
});

const composer = signal<{ mode: "create" } | { mode: "edit"; entry: AdminFaqEntry } | null>(
  null
);
const filterQuery = signal<string>("");

async function loadAll() {
  listState.value = { ...listState.value, phase: "loading", errMsg: null };
  try {
    const [list, cfg] = await Promise.all([
      api.listFaq(),
      api.getFaqConfig()
    ]);
    listState.value = {
      phase: "ready",
      entries: list.entries,
      config: cfg.config,
      errMsg: null
    };
  } catch (err) {
    listState.value = {
      ...listState.value,
      phase: "error",
      errMsg: friendlyError(err)
    };
  }
}

export function FaqView() {
  useEffect(() => {
    if (listState.value.phase === "idle") void loadAll();
  }, []);

  const state = listState.value;
  const composing = composer.value;
  const q = filterQuery.value.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return state.entries;
    return state.entries.filter(
      (e) =>
        e.question.toLowerCase().includes(q) ||
        e.answer.toLowerCase().includes(q) ||
        (e.category ?? "").toLowerCase().includes(q)
    );
  }, [state.entries, q]);

  return (
    <div class="space-y-4">
      <ConfigStrip />
      <Toolbar />

      {state.phase === "loading" && (
        <div class="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div class="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
            <div>
              <h3 class="text-sm font-bold">Falha ao carregar FAQ</h3>
              {state.errMsg && (
                <p class="text-xs text-muted-foreground">{state.errMsg}</p>
              )}
              <button
                type="button"
                onClick={() => void loadAll()}
                class="mt-1 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      )}

      {state.phase === "ready" && state.entries.length === 0 && (
        <EmptyState />
      )}

      {state.phase === "ready" &&
        state.entries.length > 0 &&
        filtered.length === 0 && (
          <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-10 text-center">
            <Search className="mx-auto mb-2 size-6 text-muted-foreground/50" />
            <h3 class="text-sm font-bold">Nada para "{filterQuery.value}"</h3>
            <button
              type="button"
              onClick={() => {
                filterQuery.value = "";
              }}
              class="mt-2 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
            >
              Limpar busca
            </button>
          </div>
        )}

      {filtered.length > 0 && (
        <ul class="space-y-2">
          {filtered.map((e, idx) => (
            <FaqRow
              key={e.id}
              entry={e}
              isFirst={idx === 0 && !q}
              isLast={idx === filtered.length - 1 && !q}
            />
          ))}
        </ul>
      )}

      {composing && (
        <ComposeModal
          mode={composing.mode}
          initial={composing.mode === "edit" ? composing.entry : null}
          onClose={() => {
            composer.value = null;
          }}
          onSaved={() => {
            composer.value = null;
            void loadAll();
          }}
        />
      )}
    </div>
  );
}

function ConfigStrip() {
  const state = listState.value;
  const cfg = state.config;
  const [busy, setBusy] = useState(false);

  async function toggle() {
    setBusy(true);
    try {
      const res = await api.saveFaqConfig(!cfg.isEnabled);
      listState.value = { ...listState.value, config: res.config };
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e, { fallback: "Falha ao salvar." })
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="flex flex-wrap items-center gap-3 rounded-md border border-border bg-card/40 px-4 py-3">
      <button
        type="button"
        onClick={() => void toggle()}
        disabled={busy}
        class={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60 ${
          cfg.isEnabled
            ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            : "border-border bg-background text-muted-foreground hover:bg-accent"
        }`}
      >
        <span
          class={`size-2.5 rounded-full ${cfg.isEnabled ? "bg-emerald-500" : "bg-muted-foreground/40"}`}
        />
        {cfg.isEnabled ? "FAQ ativo" : "FAQ desativado"}
      </button>
      <span class="hidden text-xs text-muted-foreground sm:inline">
        {cfg.isEnabled
          ? "Item 'Perguntas frequentes' aparece no menu do aluno."
          : "Aluno não vê o item — entries seguem editáveis aqui."}
      </span>
      {cfg.updatedAt && (
        <span class="ml-auto text-[11px] text-muted-foreground/80">
          Atualizado{" "}
          {new Date(cfg.updatedAt).toLocaleString("pt-BR")}
          {cfg.updatedBy && <> por {cfg.updatedBy}</>}
        </span>
      )}
    </div>
  );
}

function Toolbar() {
  return (
    <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
      <div class="relative flex-1 min-w-48">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <input
          type="search"
          value={filterQuery.value}
          onInput={(e) => {
            filterQuery.value = (e.target as HTMLInputElement).value;
          }}
          placeholder="Buscar nas perguntas/respostas/categorias…"
          class="w-full rounded-md border border-border bg-background py-1.5 pl-8 pr-7 text-xs outline-none focus:border-primary"
        />
        {filterQuery.value && (
          <button
            type="button"
            onClick={() => {
              filterQuery.value = "";
            }}
            aria-label="Limpar busca"
            class="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => void loadAll()}
        class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-2.5 py-1 text-xs font-semibold hover:bg-accent"
        title="Atualizar"
      >
        <RefreshCw width="1em" height="1em" />
        Atualizar
      </button>
      <button
        type="button"
        onClick={() => {
          composer.value = { mode: "create" };
        }}
        class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1 text-xs font-bold text-white shadow-sm hover:brightness-110"
      >
        <Plus width="1em" height="1em" />
        Nova pergunta
      </button>
    </div>
  );
}

function EmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-12 text-center">
      <HelpCircle className="mx-auto mb-2 size-8 text-muted-foreground/50" />
      <h3 class="text-sm font-bold">Nenhuma pergunta cadastrada</h3>
      <p class="mx-auto mt-1 max-w-md text-xs text-muted-foreground">
        Clique em "Nova pergunta" pra começar. Você pode formatar a resposta
        com markdown (negrito, links, listas, código).
      </p>
      <button
        type="button"
        onClick={() => {
          composer.value = { mode: "create" };
        }}
        class="mt-3 inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:brightness-110"
      >
        <Plus width="1em" height="1em" />
        Criar primeira pergunta
      </button>
    </div>
  );
}

function FaqRow({
  entry,
  isFirst,
  isLast
}: {
  entry: AdminFaqEntry;
  isFirst: boolean;
  isLast: boolean;
}) {
  const [busy, setBusy] = useState(false);

  async function togglePublished() {
    setBusy(true);
    try {
      const res = await api.updateFaq(entry.id, {
        isPublished: !entry.isPublished
      });
      listState.value = {
        ...listState.value,
        entries: listState.value.entries.map((e) =>
          e.id === entry.id ? res.entry : e
        )
      };
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e, { fallback: "Falha ao atualizar." })
      });
    } finally {
      setBusy(false);
    }
  }

  async function move(delta: -1 | 1) {
    const arr = [...listState.value.entries];
    const idx = arr.findIndex((e) => e.id === entry.id);
    const target = idx + delta;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    // Atualiza UI otimisticamente
    listState.value = { ...listState.value, entries: arr };
    try {
      await api.reorderFaq(arr.map((e) => e.id));
    } catch (e) {
      void openAlert({
        title: "Erro ao reordenar",
        message: friendlyError(e, { fallback: "Falha." })
      });
      void loadAll();
    }
  }

  async function remove() {
    const ok = await openConfirm({
      title: "Remover pergunta?",
      message: `"${entry.question}" será apagada definitivamente.`,
      confirmLabel: "Remover",
      danger: true
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.deleteFaq(entry.id);
      listState.value = {
        ...listState.value,
        entries: listState.value.entries.filter((e) => e.id !== entry.id)
      };
    } catch (e) {
      void openAlert({
        title: "Erro",
        message: friendlyError(e, { fallback: "Falha ao remover." })
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <li class="rounded-md border border-border bg-card/40">
      <div class="flex items-start gap-2 px-3 py-2.5">
        <div class="flex shrink-0 flex-col gap-0.5">
          <button
            type="button"
            onClick={() => void move(-1)}
            disabled={isFirst || busy}
            aria-label="Mover pra cima"
            title="Mover pra cima"
            class="rounded p-0.5 text-muted-foreground hover:bg-accent disabled:opacity-30"
          >
            <ArrowUp className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void move(1)}
            disabled={isLast || busy}
            aria-label="Mover pra baixo"
            title="Mover pra baixo"
            class="rounded p-0.5 text-muted-foreground hover:bg-accent disabled:opacity-30"
          >
            <ArrowDown className="size-3.5" />
          </button>
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex flex-wrap items-start gap-2">
            <h3 class="flex-1 text-sm font-semibold">
              {entry.question}
              {!entry.isPublished && (
                <span class="ml-2 rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  Rascunho
                </span>
              )}
              {entry.category && (
                <span class="ml-2 rounded-md bg-accent px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
                  {entry.category}
                </span>
              )}
            </h3>
          </div>
          <p class="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {entry.answer.replace(/\n+/g, " ")}
          </p>
        </div>

        <div class="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={() => void togglePublished()}
            disabled={busy}
            title={entry.isPublished ? "Despublicar" : "Publicar"}
            aria-label={entry.isPublished ? "Despublicar" : "Publicar"}
            class="rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            {entry.isPublished ? (
              <Eye className="size-3.5" />
            ) : (
              <EyeOff className="size-3.5" />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              composer.value = { mode: "edit", entry };
            }}
            disabled={busy}
            title="Editar"
            aria-label="Editar"
            class="rounded-md border border-border bg-background p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <Pencil className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={() => void remove()}
            disabled={busy}
            title="Remover"
            aria-label="Remover"
            class="rounded-md border border-destructive/30 bg-destructive/5 p-1.5 text-destructive hover:bg-destructive/15 disabled:opacity-50"
          >
            <Trash2 className="size-3.5" />
          </button>
        </div>
      </div>
    </li>
  );
}

function ComposeModal({
  mode,
  initial,
  onClose,
  onSaved
}: {
  mode: "create" | "edit";
  initial: AdminFaqEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [question, setQuestion] = useState(initial?.question ?? "");
  const [answer, setAnswer] = useState(initial?.answer ?? "");
  const [category, setCategory] = useState(initial?.category ?? "");
  const [isPublished, setIsPublished] = useState(initial?.isPublished ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSave() {
    if (!question.trim()) {
      setErr("Pergunta obrigatória.");
      return;
    }
    if (!answer.trim()) {
      setErr("Resposta obrigatória.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      if (mode === "create") {
        await api.createFaq({
          question: question.trim(),
          answer: answer.trim(),
          category: category.trim() || null,
          isPublished
        });
      } else if (initial) {
        await api.updateFaq(initial.id, {
          question: question.trim(),
          answer: answer.trim(),
          category: category.trim() || null,
          isPublished
        });
      }
      onSaved();
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Erro inesperado.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      // Click no backdrop NÃO fecha — feedback 2026-05-19: modais admin
      // de create/edit só fecham por botão Fechar ou ESC pra evitar perda
      // acidental do que o admin tava digitando. [[feedback-modal-no-click-outside-close]]
    >
      <div
        class="flex h-[90vh] w-full max-w-3xl flex-col rounded-t-2xl bg-background shadow-2xl sm:h-auto sm:max-h-[85vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center gap-3 border-b border-border px-4 py-3">
          <HelpCircle className="size-5 text-primary" />
          <h2 class="flex-1 text-base font-bold">
            {mode === "create" ? "Nova pergunta" : "Editar pergunta"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Fechar"
            class="rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          <div class="space-y-1.5">
            <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Pergunta <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={question}
              onInput={(e) => setQuestion((e.target as HTMLInputElement).value)}
              maxLength={300}
              placeholder="Ex: Como ativo o modo offline?"
              disabled={busy}
              autoFocus
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <div class="text-right text-[10px] tabular-nums text-muted-foreground/70">
              {question.length}/300
            </div>
          </div>

          <div class="grid gap-3 sm:grid-cols-2">
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Categoria (opcional)
              </label>
              <input
                type="text"
                value={category}
                onInput={(e) =>
                  setCategory((e.target as HTMLInputElement).value)
                }
                maxLength={80}
                placeholder="Ex: Conta, Pagamento, Estudo"
                disabled={busy}
                class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60"
              />
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Visibilidade
              </label>
              <button
                type="button"
                onClick={() => setIsPublished(!isPublished)}
                disabled={busy}
                class={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-60 ${
                  isPublished
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                    : "border-border bg-background text-muted-foreground hover:bg-accent"
                }`}
              >
                {isPublished ? (
                  <Eye className="size-4" />
                ) : (
                  <EyeOff className="size-4" />
                )}
                {isPublished ? "Publicado" : "Rascunho"}
              </button>
            </div>
          </div>

          <div class="space-y-1.5">
            <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Resposta <span class="text-red-500">*</span>
              <span class="ml-2 font-normal normal-case text-muted-foreground/70">
                — markdown: **negrito**, *itálico*, [link](url), `código`,
                listas com - / 1.
              </span>
            </label>
            <textarea
              value={answer}
              onInput={(e) =>
                setAnswer((e.target as HTMLTextAreaElement).value)
              }
              maxLength={10000}
              rows={12}
              placeholder="Conta com calma. Você pode usar markdown e colocar links."
              disabled={busy}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 font-mono text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <div class="text-right text-[10px] tabular-nums text-muted-foreground/70">
              {answer.length}/10000
            </div>
          </div>

          {err && (
            <div class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
              <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <p class="text-xs">{err}</p>
            </div>
          )}
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border bg-card/30 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold hover:bg-accent disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !question.trim() || !answer.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60"
          >
            {busy ? (
              <Loader2 className="size-4 animate-spin" />
            ) : mode === "create" ? (
              <CheckCircle2 className="size-4" />
            ) : (
              <Save className="size-4" />
            )}
            {mode === "create" ? "Criar" : "Salvar"}
          </button>
        </footer>
      </div>
    </div>
  );
}
